import { isEqual } from 'lodash-es';
import { useContext, useRef } from 'react';

import { useStoreSelector } from '../../../../core/store/selector.js';
import { CategoricalBarChartStateContext } from '../../contexts/CategoricalBarChartState.context.js';
import {
  type CategoricalBarChartMousePositionState,
  CategoricalBarChartState,
} from '../../types/state.js';

export const useSelector = <T>(
  selector: (state: CategoricalBarChartState) => T,
  isEqual?: (a: T, b: T) => boolean,
): T => useStoreSelector(CategoricalBarChartStateContext, selector, isEqual);

export const useHighlightedDimensionState = () =>
  useSelector((state: CategoricalBarChartState) => state.highlightedDimension);

export const useFilteredDimensionsState = () =>
  useSelector((state: CategoricalBarChartState) => state.filteredDimensions);

export const useTooltipState = () =>
  useSelector(
    (state) => ({
      pinnedTooltip: state.overlays.pinnedTooltip,
      visibleTooltip: state.overlays.visibleTooltip,
    }),
    isEqual,
  );

export const useHoveredThresholdIdState = () =>
  useSelector((state: CategoricalBarChartState) => state.hoveredThresholdId);

export const useSetState = () => {
  const { setState } = useContext(CategoricalBarChartStateContext);

  return setState;
};

export const useMousePositionState = () => {
  const ref = useRef<CategoricalBarChartMousePositionState>(undefined);

  return useSelector((state: CategoricalBarChartState) => {
    if (!state.overlays.pinnedTooltip) {
      ref.current = state.overlays.mousePosition;
    }
    return ref.current;
  }, isEqual);
};

export const useMouseInBoundsState = () =>
  useSelector(
    (state: CategoricalBarChartState) => state.overlays.mouseInBounds,
  );
